/**
 * Credit Management Section for Superadmin
 * 
 * Features:
 * - Display current Stripe balance
 * - Add credit to customer
 * - Apply Stripe coupons
 * - View credit history with pagination
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  DollarSign, Plus, Ticket, History, Loader2, CheckCircle,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreditManagementSection = ({ customerId, customerName }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [activeCoupons, setActiveCoupons] = useState([]);
  
  // Credit form
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [addingCredit, setAddingCredit] = useState(false);
  
  // Coupon dropdown
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  
  // History
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPages, setHistoryPages] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (customerId) {
      fetchBalance();
      fetchCoupons();
      fetchHistory(1);
    }
  }, [customerId]);

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/customers/${customerId}/balance`);
      setBalance(response.data.balance_euros || 0);
      setActiveCoupons(response.data.active_coupons || []);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      setBalance(0);
      setActiveCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await axios.get(`${API}/superadmin/coupons`);
      setCoupons(response.data.coupons || []);
    } catch (error) {
      // Silent fallback - use mock coupons for preview
      setCoupons([
        { id: 'SOMMER2026', name: 'Sommer-Rabatt 2026', display: '20% Rabatt', duration_display: '3 Monate', valid: true },
        { id: 'NEUKUNDE', name: 'Neukundenrabatt', display: '10% Rabatt', duration_display: 'Einmalig', valid: true },
        { id: 'WINTER10', name: 'Winter-Aktion', display: '10€ Rabatt', duration_display: 'Dauerhaft', valid: true }
      ]);
      console.info('ℹ️ Stripe nicht verbunden: Verwende Test-Gutscheine');
    } finally {
      setLoadingCoupons(false);
    }
  };

  const fetchHistory = async (page) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API}/superadmin/customers/${customerId}/credit-history?page=${page}&limit=5`
      );
      setHistory(response.data.transactions || []);
      setHistoryPage(page);
      setHistoryTotal(response.data.pagination?.total || 0);
      setHistoryPages(response.data.pagination?.pages || 0);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAddCredit = async () => {
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error('Bitte gültigen Betrag eingeben');
      return;
    }
    if (!creditReason.trim()) {
      toast.error('Bitte Grund angeben');
      return;
    }
    const amount = parseFloat(creditAmount);
    if (amount > 200) {
      toast.error('Maximaler Betrag: 200€');
      return;
    }

    setAddingCredit(true);
    try {
      await axios.post(`${API}/superadmin/customers/${customerId}/credit`, {
        amount_euros: amount,
        reason: creditReason,
        send_email: sendEmail
      });
      toast.success(`${amount}€ erfolgreich gutgeschrieben`);
      setCreditAmount('');
      setCreditReason('');
      fetchBalance();
      fetchHistory(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Gutschreiben');
    } finally {
      setAddingCredit(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!selectedCoupon) {
      toast.error('Bitte Gutschein auswählen');
      return;
    }
    setApplyingCoupon(true);
    try {
      await axios.post(`${API}/superadmin/customers/${customerId}/apply-coupon`, {
        coupon_id: selectedCoupon,
        send_email: sendEmail
      });
      toast.success('Gutschein erfolgreich angewendet');
      setSelectedCoupon('');
      fetchBalance();
      fetchHistory(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anwenden');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'balance_credit': return <DollarSign className="h-4 w-4 text-green-600" />;
      case 'coupon_applied': return <Ticket className="h-4 w-4 text-purple-600" />;
      case 'promotion_code_redeemed': return <CheckCircle className="h-4 w-4 text-purple-600" />;
      default: return <History className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Balance Hero ──────────────────────────────────────── */}
      <div className="rounded-lg border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-1">
            Stripe-Kontoguthaben
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-green-700 dark:text-green-300">
              {balance.toFixed(2)}€
            </span>
            <span className="text-xs text-green-600 dark:text-green-400">
              {balance > 0
                ? "wird automatisch von nächster Abo-Rechnung abgezogen"
                : "kein offenes Guthaben"}
            </span>
          </div>
        </div>
        <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
          <DollarSign className="h-7 w-7 text-green-600 dark:text-green-400" />
        </div>
      </div>

      {/* Active Coupons */}
      {activeCoupons.length > 0 && (
        <div className="rounded-lg border-2 border-purple-200 bg-purple-50 dark:bg-purple-950 p-3">
          <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-2 flex items-center gap-1.5">
            <Ticket className="h-3.5 w-3.5" />
            Aktive Rabatte
          </p>
          <div className="flex flex-wrap gap-2">
            {activeCoupons.map((coupon, index) => (
              <Badge key={index} className="bg-purple-100 hover:bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 border border-purple-300">
                <Ticket className="h-3 w-3 mr-1" />
                {coupon.name}: {coupon.discount}
                {coupon.end_date && ` (bis ${coupon.end_date})`}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* ── Stripe-Balance-Anpassung ──────────────────────────────────── */}
      <div className="rounded-lg border-l-4 border-l-blue-500 border border-blue-100 bg-blue-50/40 dark:bg-blue-950/20 p-4">
        <h4 className="text-sm font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-400 mb-1">
          <Plus className="h-4 w-4" />
          Stripe-Guthaben hinzufügen
        </h4>
        <p className="text-xs text-muted-foreground mb-3">
          Nur für Kompensationen & Korrekturen — kein Pay-per-Use. Betrag wird automatisch von nächster Abo-Rechnung abgezogen.
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="credit-amount" className="text-xs font-medium">Betrag (€)</Label>
              <Input
                id="credit-amount"
                type="number"
                step="0.01"
                min="0.01"
                max="200"
                placeholder="5.00"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                className="h-9 bg-white dark:bg-gray-900"
              />
              <p className="text-xs text-muted-foreground">Max: 200€</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="credit-reason" className="text-xs font-medium">Grund</Label>
              <Input
                id="credit-reason"
                placeholder="z.B. Entschädigung"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                className="h-9 bg-white dark:bg-gray-900"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendEmailCredit"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="sendEmailCredit" className="text-xs cursor-pointer font-normal">
              E-Mail-Benachrichtigung senden
            </Label>
          </div>
          <Button 
            onClick={handleAddCredit} 
            disabled={addingCredit}
            size="sm"
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {addingCredit
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Wird gutgeschrieben...</>
              : <><Plus className="h-3.5 w-3.5 mr-1.5" /> Stripe-Guthaben gutschreiben</>
            }
          </Button>
        </div>
      </div>

      {/* ── Assign Coupon ─────────────────────────────────────── */}
      <div className="rounded-lg border-l-4 border-l-purple-500 border border-purple-100 bg-purple-50/40 dark:bg-purple-950/20 p-4">
        <h4 className="text-sm font-semibold flex items-center gap-2 text-purple-700 dark:text-purple-400 mb-3">
          <Ticket className="h-4 w-4" />
          Gutschein zuweisen
        </h4>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="coupon-select" className="text-xs font-medium">Gutschein auswählen</Label>
            <Select value={selectedCoupon} onValueChange={setSelectedCoupon}>
              <SelectTrigger id="coupon-select" className="h-9 bg-white dark:bg-gray-900">
                <SelectValue placeholder={loadingCoupons ? "Laden..." : "Gutschein wählen"} />
              </SelectTrigger>
              <SelectContent>
                {coupons.filter(c => c.valid).map((coupon) => (
                  <SelectItem key={coupon.id} value={coupon.id}>
                    {coupon.name} – {coupon.display} ({coupon.duration_display})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleApplyCoupon} 
            disabled={applyingCoupon || !selectedCoupon}
            size="sm"
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            {applyingCoupon
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Anwenden...</>
              : <><Ticket className="h-3.5 w-3.5 mr-1.5" /> Gutschein zuweisen</>
            }
          </Button>
        </div>
      </div>

      {/* ── Transaction History ───────────────────────────────── */}
      <div className="rounded-lg border-l-4 border-l-gray-400 border border-gray-100 dark:border-gray-800 p-4">
        <h4 className="text-sm font-semibold flex items-center gap-2 text-gray-700 dark:text-gray-300 mb-3">
          <History className="h-4 w-4" />
          Transaktions-Historie
        </h4>

        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : history.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="rounded-full bg-muted p-3 mb-3">
              <History className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">Noch keine Transaktionen</p>
            <p className="text-xs text-muted-foreground mt-1">
              Gutschriften und Rabatte erscheinen hier
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {history.map((transaction) => (
                <div 
                  key={transaction.id} 
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {getTransactionIcon(transaction.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-tight">
                          {transaction.type === 'balance_credit' && 'Guthaben gutgeschrieben'}
                          {transaction.type === 'coupon_applied' && 'Gutschein angewendet'}
                          {transaction.type === 'promotion_code_redeemed' && 'Code eingelöst'}
                        </p>
                        {transaction.reason && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{transaction.reason}</p>
                        )}
                        {transaction.coupon_name && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{transaction.coupon_name}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(transaction.created_at)}
                          {transaction.created_by_email && ` • ${transaction.created_by_email}`}
                        </p>
                      </div>
                      {transaction.amount_euros && (
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-green-600">
                            +{transaction.amount_euros.toFixed(2)}€
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {historyPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  Seite {historyPage} von {historyPages} ({historyTotal} Einträge)
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchHistory(historyPage - 1)}
                    disabled={historyPage === 1}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchHistory(historyPage + 1)}
                    disabled={historyPage === historyPages}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};
